import { rooms, WASH_PAYMENT_WARNING_LABELS, type WashWorkOrder } from "@station/contracts";
import { Money } from "@station/core";
import { Alert, Button, Card, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Ban, Pencil } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useWashPermissions, useWashStations, useWashWorkOrder } from "../../../../features/wash/queries.js";
import {
  Field,
  ReadySmsBadge,
  TableFrame,
  Th,
  washCrumb,
  WashPage,
  WorkOrderStatusBadge,
} from "../../../../features/wash/shared.js";
import {
  CancelWorkOrderDialog,
  DeliverDialog,
  useWorkOrderTransition,
  WorkOrderFormDialog,
} from "../../../../features/wash/work-order-dialogs.js";
import { WorkOrderPhotos } from "../../../../features/wash/work-order-photos.js";
import {
  displayPlate,
  formatDuration,
  forwardTransition,
  isWorkOrderEditable,
  paymentState,
} from "../../../../features/wash/wash-values.js";
import { useRoom } from "../../../../lib/realtime-context.js";
import { usePermissions } from "../../../../lib/session.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object({ station: z.string().uuid().optional().catch(undefined) });

export const Route = createFileRoute("/_app/yikama/is-emirleri/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: WorkOrderRoute,
});

/**
 * İş emri ayrıntısı (Prompt 18 web, ADR 0018 §13): hizmet satırları (WAITING / IN_PROGRESS iken düzenlenir; fiyat anlık
 * görüntüdür), personel, geçiş zaman çizelgesi, durum makinesine göre düğmeler (Başla → Hazır → Teslim et; İptal
 * gerekçeyle), hazır SMS'inin sonucu, kasa ödeme bağı + tutar uyarısı, ödemesiz teslim gerekçesi ve önce / sonra
 * fotoğrafları. Kişisel alanlar yetkiye göre maskelidir.
 */
function WorkOrderRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const permissions = useWashPermissions();
  const order = useWashWorkOrder(id);
  useRoom(order.data ? rooms.station(order.data.stationId) : null);
  const title = order.data
    ? t("wash.workOrders.detailTitle", { no: order.data.orderNo })
    : t("menu.washWorkOrders");
  return (
    <WashPage
      title={title}
      breadcrumb={[
        washCrumb(
          t("menu.washWorkOrders"),
          "/yikama/is-emirleri",
          search.station ? { station: search.station } : {},
        ),
        { label: order.data?.orderNo ?? "…" },
      ]}
    >
      {order.error ? (
        <Alert tone="danger">{errorMessage(order.error)}</Alert>
      ) : !order.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <WorkOrderBody order={order.data} canOperate={permissions.canOperate} />
      )}
    </WashPage>
  );
}

function WorkOrderBody({ order, canOperate }: { order: WashWorkOrder; canOperate: boolean }): ReactElement {
  const { t } = useTranslation();
  const stations = useWashStations();
  const station = stations.data?.find((candidate) => candidate.id === order.stationId) ?? null;
  const transition = useWorkOrderTransition();
  // Müşteri kartı `customers.read` ister (yıkama görevlisinde yok): bağlantı yalnızca açabilene gösterilir.
  const canOpenCustomer = usePermissions().has("customers.read");
  const [dialog, setDialog] = useState<"edit" | "deliver" | "cancel" | null>(null);
  const next = forwardTransition(order.status, order.allowedTransitions);
  const canCancel = order.allowedTransitions.includes("CANCELLED");
  const payment = paymentState(order);
  const plate = displayPlate(order.plate, order.personalDataMasked);
  const paidDiffers =
    order.paidAmount !== null && !Money.fromMinor(order.paidAmount).eq(Money.fromMinor(order.totalAmount));

  const timeline: Array<{
    key: string;
    label: string;
    at: string | null;
    extra?: ReactElement | string | null;
  }> = [
    { key: "waiting", label: t("wash.workOrders.timeline.WAITING"), at: order.waitingAt },
    { key: "progress", label: t("wash.workOrders.timeline.IN_PROGRESS"), at: order.inProgressAt },
    {
      key: "ready",
      label: t("wash.workOrders.timeline.READY"),
      at: order.readyAt,
      extra: order.readyAt ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {t("wash.workOrders.sms")}: <ReadySmsBadge status={order.readySmsStatus} />
          {order.readySmsAt ? (
            <span className="text-xs text-muted-foreground">{formatDateTime(order.readySmsAt)}</span>
          ) : null}
        </span>
      ) : null,
    },
    {
      key: "delivered",
      label: t("wash.workOrders.timeline.DELIVERED"),
      at: order.deliveredAt,
      extra: order.unpaidDeliveryReason
        ? t("wash.workOrders.unpaidNote", { reason: order.unpaidDeliveryReason })
        : null,
    },
    ...(order.cancelledAt
      ? [
          {
            key: "cancelled",
            label: t("wash.workOrders.timeline.CANCELLED"),
            at: order.cancelledAt,
            extra: order.cancelReason
              ? t("wash.workOrders.cancelNote", { reason: order.cancelReason })
              : null,
          },
        ]
      : []),
  ];

  return (
    <>
      <Card className="grid gap-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-2xl font-semibold tracking-wide" data-testid="work-order-plate">
            {plate}
          </span>
          <WorkOrderStatusBadge status={order.status} />
          {order.status === "READY" ? <ReadySmsBadge status={order.readySmsStatus} /> : null}
          <span className="font-mono text-sm text-muted-foreground">{order.orderNo}</span>
        </div>
        {order.personalDataMasked ? <Alert tone="info">{t("wash.workOrders.masked")}</Alert> : null}
        {order.personalDataErasedAt ? (
          <Alert tone="info">
            {t("wash.workOrders.erased", { at: formatDateTime(order.personalDataErasedAt) })}
          </Alert>
        ) : null}
        {canOperate && (next || canCancel || isWorkOrderEditable(order.status)) ? (
          <div className="flex flex-wrap gap-2" data-testid="work-order-actions">
            {next ? (
              <Button
                size="lg"
                loading={transition.isPending}
                onClick={() =>
                  next === "DELIVERED" ? setDialog("deliver") : transition.mutate({ order, to: next })
                }
              >
                {t(`wash.workOrders.forward.${next}`)}
              </Button>
            ) : null}
            {isWorkOrderEditable(order.status) && station ? (
              <Button size="lg" variant="outline" onClick={() => setDialog("edit")}>
                <Pencil aria-hidden="true" />
                {t("common.edit")}
              </Button>
            ) : null}
            {canCancel ? (
              <Button size="lg" variant="ghost" onClick={() => setDialog("cancel")}>
                <Ban aria-hidden="true" />
                {t("wash.workOrders.cancel")}
              </Button>
            ) : null}
          </div>
        ) : null}
        {transition.error ? <Alert tone="danger">{errorMessage(transition.error)}</Alert> : null}
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("wash.fields.unit")}>{order.unitName ?? "—"}</Field>
          <Field label={t("wash.fields.staff")}>{order.staffName ?? "—"}</Field>
          <Field label={t("wash.fields.contactName")}>{order.contactName ?? "—"}</Field>
          <Field label={t("wash.fields.contactPhone")}>{order.contactPhone ?? "—"}</Field>
          <Field label={t("wash.fields.customer")}>
            {order.customerProfileId && canOpenCustomer ? (
              <Link
                to="/musteriler/$id"
                params={{ id: order.customerProfileId }}
                className="underline underline-offset-4"
              >
                {t("wash.workOrders.openCustomer")}
              </Link>
            ) : order.customerProfileId ? (
              t("wash.workOrders.customerLinked")
            ) : (
              "—"
            )}
          </Field>
          <Field label={t("wash.workOrders.appointment")}>
            {order.appointmentStartsAt
              ? formatDateTime(order.appointmentStartsAt)
              : t("wash.workOrders.walkIn")}
          </Field>
          <Field label={t("wash.workOrders.eta")}>
            {order.estimatedReadyAt ? formatDateTime(order.estimatedReadyAt) : "—"}
          </Field>
          <Field label={t("wash.fields.station")}>{order.stationName}</Field>
        </dl>
        {order.note ? (
          <p className="rounded-md bg-muted/50 p-3 text-sm">
            <span className="font-medium">{t("wash.fields.note")}: </span>
            {order.note}
          </p>
        ) : null}
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] [&>*]:min-w-0">
        <Card className="grid content-start gap-3 p-4">
          <h2 className="text-base font-semibold">{t("wash.workOrders.lines")}</h2>
          <TableFrame label={t("wash.workOrders.lines")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("wash.fields.program")}</Th>
                <Th align="right">{t("wash.workOrders.quantityShort")}</Th>
                <Th align="right">{t("wash.workOrders.unitPrice")}</Th>
                <Th align="right">{t("wash.workOrders.amount")}</Th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((line) => (
                <tr key={line.lineNo} className="border-t">
                  <td className="px-3 py-2">
                    <span className="grid gap-0.5">
                      <span>{line.programName}</span>
                      <span className="text-xs text-muted-foreground">
                        {line.programCode}
                        {line.durationSeconds ? ` · ${formatDuration(line.durationSeconds)}` : ""}
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular">{formatCount(line.quantity)}</td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={line.unitPrice} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={line.amount} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 font-semibold">
              <tr>
                <th scope="row" colSpan={3} className="px-3 py-2 text-left">
                  {t("common.total")}
                </th>
                <td className="px-3 py-2 text-right" data-testid="work-order-total">
                  <MoneyText value={order.totalAmount} />
                </td>
              </tr>
            </tfoot>
          </TableFrame>
          <p className="text-xs text-muted-foreground">{t("wash.workOrders.priceSnapshotHint")}</p>
        </Card>

        <div className="grid content-start gap-4">
          <Card className="grid gap-3 p-4" data-testid="work-order-payment">
            <h2 className="text-base font-semibold">{t("wash.workOrders.paymentTitle")}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge
                tone={payment === "paid" ? "success" : payment === "warning" ? "warning" : "neutral"}
              >
                {t(`wash.workOrders.payment.${payment}`)}
              </StatusBadge>
              {order.posSaleId ? (
                <Link
                  to="/kasa/satislar/$id"
                  params={{ id: order.posSaleId }}
                  className="text-sm underline underline-offset-4"
                >
                  {t("wash.activations.openSale")}
                </Link>
              ) : null}
            </div>
            {order.paidAmount !== null ? (
              <p className="text-sm">
                {t("wash.workOrders.paidAmount")}: <MoneyText value={order.paidAmount} /> /{" "}
                <MoneyText value={order.totalAmount} />
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("wash.workOrders.paymentHint", { no: order.orderNo })}
              </p>
            )}
            {order.paymentWarning ? (
              <Alert tone="warning">
                {order.paymentWarningMessage ?? WASH_PAYMENT_WARNING_LABELS[order.paymentWarning]}
              </Alert>
            ) : paidDiffers ? (
              <Alert tone="warning">{WASH_PAYMENT_WARNING_LABELS.AMOUNT_MISMATCH}</Alert>
            ) : null}
          </Card>
          <Card className="grid gap-3 p-4">
            <h2 className="text-base font-semibold">{t("wash.workOrders.timelineTitle")}</h2>
            <ol className="grid gap-3" aria-label={t("wash.workOrders.timelineTitle")}>
              {timeline.map((step) => (
                <li key={step.key} className="grid grid-cols-[auto_1fr] gap-3">
                  <span
                    aria-hidden="true"
                    className={`mt-1.5 size-2.5 rounded-full ${step.at ? "bg-primary" : "border bg-card"}`}
                  />
                  <span className="grid gap-0.5 text-sm">
                    <span className="font-medium">{step.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {step.at ? formatDateTime(step.at) : t("wash.workOrders.notYet")}
                    </span>
                    {step.extra ? <span className="text-xs">{step.extra}</span> : null}
                  </span>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>

      <WorkOrderPhotos order={order} canOperate={canOperate} />

      {dialog === "edit" && station ? (
        <WorkOrderFormDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          station={station}
          order={order}
        />
      ) : null}
      {dialog === "deliver" ? (
        <DeliverDialog open onOpenChange={(open) => !open && setDialog(null)} order={order} />
      ) : null}
      {dialog === "cancel" ? (
        <CancelWorkOrderDialog open onOpenChange={(open) => !open && setDialog(null)} order={order} />
      ) : null}
    </>
  );
}
